import { useEffect, useState } from "react";
import { useNotification } from "@hooks/useNotifications";
import { workspacesService } from "features/workspaces/services/workspaces-service";
import type { IEditorFetchParams, ITranslationItemResponse } from "features/workspaces/types";

export const useFetchSimilarTranslations = (
  fetchParams: IEditorFetchParams,
  id: number,
  setIsLoading: (loading: boolean) => void,
): {
  similarTranslations: ITranslationItemResponse[];
} => {
  const [similarTranslations, setSimilarTranslations] = useState<ITranslationItemResponse[]>([]);

  const { showError } = useNotification();

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await workspacesService.getTranslationItems(fetchParams, abortController);

        if (!abortController.signal.aborted && Array.isArray(response?.list)) {
          // Remove the current item from the list
          const list = response.list.filter(item => item.original.id !== id);
          setSimilarTranslations(list);
        }
      } catch (error) {
        if (!abortController?.signal.aborted) {
          if (error?.response?.status !== 404) {
            console.error(error);

            showError("Failed to fetch similar translation items.");
          }

          setSimilarTranslations([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [fetchParams]);

  return {
    similarTranslations,
  };
};
